import Link from 'next/link'

import { cn } from '@/utilities/ui'

import type { ReactNode } from 'react'

type KickerSize = 'default' | 'small'
type KickerTone = 'default' | 'inverse'

type KickerBaseProps = {
  children: ReactNode
  size?: KickerSize
  href?: string
  className?: string
}

type KickerProps =
  | (KickerBaseProps & { variant?: 'default'; tone?: KickerTone })
  | (KickerBaseProps & { variant: 'filled'; tone?: KickerTone })

const sizeVariants: Record<KickerSize, string> = {
  default: 'text-base font-medium leading-5 px-3 py-1 min-h-8',
  small: 'text-xs font-medium leading-4 px-2 py-1 min-h-6',
}

const defaultToneVariants: Record<KickerTone, string> = {
  default: 'bg-transparent text-muted-foreground border-border',
  inverse: 'bg-transparent text-background border-outline-inverse-border',
}
const filledToneVariants: Record<KickerTone, string> = {
  default: 'bg-foreground text-background border-border',
  inverse: 'bg-card text-card-foreground border-card',
}

export const Kicker: React.FC<KickerProps> = (props: KickerProps) => {
  const { children, size = 'default', href, className } = props

  const variantClasses =
    props.variant === 'filled'
      ? filledToneVariants[props.tone ?? 'default']
      : defaultToneVariants[props.tone ?? 'default']

  if (href) {
    return (
      <Link
        href={href}
        className={cn(
          'flex items-center gap-2 rounded-full border font-body transition-all duration-200 hover:bg-neutral-100 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen-500 focus-visible:ring-offset-0',
          sizeVariants[size],
          variantClasses,
          className,
        )}
      >
        {children}
      </Link>
    )
  }

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-full border font-body',
        sizeVariants[size],
        variantClasses,
        className,
      )}
    >
      {children}
    </div>
  )
}
